import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useSiteSettings from "@/hooks/useSiteSettings";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIME_SLOTS = [
  "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "1:00 PM", "1:30 PM",
  "2:00 PM", "2:30 PM", "3:00 PM", "3:30 PM", "4:00 PM",
];

export default function Booking({ selectedPackage, onChangePackage }) {
  const { settings } = useSiteSettings();
  const PKG_OPTIONS = settings.packages.map((p) => ({
    id: p.id,
    label: `${p.name} Package — $${Number(p.price).toFixed(0)}`,
  }));

  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    vehicle: "",
    notes: "",
  });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedPackage) return toast.error("Please select a package");
    if (!date) return toast.error("Please pick an appointment date");
    if (!time) return toast.error("Please pick a time slot");
    if (!form.full_name || !form.email || !form.phone || !form.vehicle) {
      return toast.error("Please fill in all required fields");
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/bookings`, {
        ...form,
        package_id: selectedPackage,
        appointment_date: format(date, "yyyy-MM-dd"),
        appointment_time: time,
        origin_url: window.location.origin,
      });
      if (data.booking_id) {
        setSubmitted(true);
        // reset form
        setForm({ full_name: "", email: "", phone: "", vehicle: "", notes: "" });
        setDate(null);
        setTime("");
        onChangePackage(null);
      } else {
        toast.error("Could not submit booking");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit booking");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-[#262626] focus:border-[#D6B98D] focus:ring-1 focus:ring-[#D6B98D]/40 rounded-sm px-4 py-3.5 text-sm text-white placeholder-white/30 outline-none transition-colors";

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-20 lg:py-28 px-6 lg:px-12 bg-[#FDF9F3]"
    >
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <div className="font-hand text-2xl text-[#A67F4E] mb-2">let&apos;s do it</div>
          <h2
            data-testid="booking-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#3D2E1C] leading-[1.05] mb-6"
          >
            Book your detail.
          </h2>
          <p className="text-[#3D2E1C]/70 leading-relaxed mb-6">
            Pick a package, date, and time. We&apos;ll review and confirm your appointment
            by email — you pay in person via tap-to-pay after we&apos;re done.
          </p>
          <ul className="space-y-2 text-sm text-[#7A6A55]">
            <li>· Appointments take 1.5–4 hours</li>
            <li>· Service at our Portland studio</li>
            <li>· Reschedule up to 24 hours before</li>
            <li>· Pay with card, Apple Pay, or Google Pay after</li>
          </ul>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} data-testid="booking-form" className="space-y-5 p-6 lg:p-8 bg-white border-2 border-[#E9DFCB] rounded-3xl">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#A67F4E] mb-2">
                Package
              </label>
              <Select value={selectedPackage || ""} onValueChange={onChangePackage}>
                <SelectTrigger data-testid="package-select" className={inputCls}>
                  <SelectValue placeholder="Choose a package" />
                </SelectTrigger>
                <SelectContent className="bg-white border-[#E9DFCB] text-[#3D2E1C]">
                  {PKG_OPTIONS.map((o) => (
                    <SelectItem key={o.id} value={o.id} data-testid={`pkg-option-${o.id}`}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#A67F4E] mb-2">
                Appointment date
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="date-picker-trigger"
                    className={`${inputCls} text-left flex items-center justify-between`}
                  >
                    <span className={date ? "text-[#3D2E1C]" : "text-[#7A6A55]/60"}>
                      {date ? format(date, "PPP") : "Pick a date"}
                    </span>
                    <CalendarIcon className="w-4 h-4 text-[#A67F4E]" strokeWidth={2} />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-white border-[#E9DFCB]" align="start">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => {
                      const minDate = new Date();
                      minDate.setHours(0, 0, 0, 0);
                      minDate.setDate(minDate.getDate() + 2);
                      return d < minDate;
                    }}
                    initialFocus
                    className="text-[#3D2E1C]"
                  />
                </PopoverContent>
              </Popover>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#A67F4E] mb-2">
                  Time slot
                </label>
                <Select value={time} onValueChange={setTime}>
                  <SelectTrigger data-testid="time-select" className={inputCls}>
                    <SelectValue placeholder="Pick a time" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#E9DFCB] text-[#3D2E1C]">
                    {TIME_SLOTS.map((t) => (
                      <SelectItem key={t} value={t} data-testid={`time-option-${t.replace(/[: ]/g, "-")}`}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#A67F4E] mb-2">
                  Full name
                </label>
                <input
                  data-testid="input-name"
                  type="text"
                  value={form.full_name}
                  onChange={update("full_name")}
                  className={inputCls}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-[#A67F4E] mb-2">
                  Phone
                </label>
                <input
                  data-testid="input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  className={inputCls}
                  placeholder="(503) 555-0123"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#A67F4E] mb-2">
                Email
              </label>
              <input
                data-testid="input-email"
                type="email"
                value={form.email}
                onChange={update("email")}
                className={inputCls}
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#A67F4E] mb-2">
                Vehicle (make, model, year)
              </label>
              <input
                data-testid="input-vehicle"
                type="text"
                value={form.vehicle}
                onChange={update("vehicle")}
                className={inputCls}
                placeholder="e.g. 2021 BMW M3"
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[#A67F4E] mb-2">
                Notes (optional)
              </label>
              <textarea
                data-testid="input-notes"
                value={form.notes}
                onChange={update("notes")}
                rows={3}
                className={`${inputCls} resize-none`}
                placeholder="Anything we should know?"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="booking-submit"
              className="w-full mt-2 inline-flex items-center justify-center gap-3 py-4 bg-[#3D2E1C] text-[#FDF9F3] hover:bg-[#A67F4E] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 text-sm font-bold rounded-full"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                </>
              ) : (
                "Request Appointment →"
              )}
            </button>

            <p className="text-xs text-[#7A6A55] text-center mt-2">
              No payment now. We&apos;ll confirm by email. Pay in person after your detail.
            </p>
          </form>
        </div>
      </div>

      {submitted && (
        <div
          data-testid="booking-success-modal"
          className="fixed inset-0 z-50 bg-[#3D2E1C]/70 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => setSubmitted(false)}
        >
          <div
            className="w-full max-w-md bg-white border-2 border-[#D6B98D] rounded-3xl p-8 text-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-16 h-16 rounded-full bg-[#F5EBD9] mx-auto mb-5 flex items-center justify-center">
              <svg className="w-8 h-8 text-[#A67F4E]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="font-hand text-2xl text-[#A67F4E] mb-1">nice!</div>
            <h3 className="font-serif text-3xl text-[#3D2E1C] font-bold mb-3">Booking received</h3>
            <p className="text-[#3D2E1C]/70 text-sm leading-relaxed mb-6">
              Thanks! We&apos;ll review and confirm your appointment shortly.
              You&apos;ll get a calendar invite by email once it&apos;s approved.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              data-testid="booking-success-close"
              className="inline-flex items-center px-7 py-3 bg-[#3D2E1C] text-[#FDF9F3] hover:bg-[#A67F4E] text-sm font-bold rounded-full transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
